import { useState, useEffect } from "react";
import { SLOT_PHOTOS } from "@/data/slotPhotos";
import {
  getSavedDates, saveDates, loadTripDates, saveTripDates,
  todayYmd, addDaysYmd, isAfterYmd, daysBetweenYmd, areDatesValid,
} from "@/lib/tripDates";
import { useNavigate, Link } from "@tanstack/react-router";
import { Users, ChevronRight, Check, X } from "lucide-react";
import type { VehicleFamily, RoofHeight, TVOption } from "@/data/vehicleFamilies";
import { TV_LABEL, resolveCrmVehicleId } from "@/data/vehicleFamilies";
import { useFamilyPricing, useRateAdjustments, applyRateAdjustments, type AppliedAdjustment } from "@/hooks/usePricing";
import SeatingDiagram from "./SeatingDiagram";
import sprinterDiag12Std from "@/assets/sprinter-diagrams/sprinter-12-standard.png";
import sprinterDiag12High from "@/assets/sprinter-diagrams/sprinter-12-high.png";
import sprinterDiag15High from "@/assets/sprinter-diagrams/sprinter-15-high.png";
import expressDiag12 from "@/assets/express-diagrams/express-12.png";
import expressDiag15 from "@/assets/express-diagrams/express-15.png";
import transitDiag12 from "@/assets/van-diagrams/transit-12.png";
import transitDiag15 from "@/assets/van-diagrams/transit-15.png";
import metrisDiag8 from "@/assets/metris-diagrams/metris-8.png";

export function getDiagram(slug: string, passengers: number, roof: RoofHeight): string | null {
  if (slug === "mercedes-metris") return metrisDiag8;
  if (slug === "mercedes-sprinter-executive") {
    return passengers >= 10 ? sprinterDiag15High : sprinterDiag12High;
  }
  if (slug === "mercedes-sprinter" || slug === "mercedes-sprinter-older") {
    if (passengers === 15) return sprinterDiag15High;
    return roof === "high" ? sprinterDiag12High : sprinterDiag12Std;
  }
  if (slug === "chevy-express") return passengers === 15 ? expressDiag15 : expressDiag12;
  // All Ford Transit roof variants use the mid-roof diagram
  if (slug === "ford-transit" || slug === "ford-transit-low" || slug === "ford-transit-high") {
    return passengers === 15 ? transitDiag15 : transitDiag12;
  }
  return null;
}

interface Props {
  family: VehicleFamily;
  open: boolean;
  onClose: () => void;
  city?: string;
  accent?: string;
}

export default function ConfigureOptionsSheet({ family, open, onClose, city, accent = "#F97316" }: Props) {
  const [passengers, setPassengers] = useState<number>(family.passengerOptions[0]);
  const [roof, setRoof] = useState<RoofHeight>(family.roofOptions[0]);
  const [tv, setTv] = useState<TVOption>(() => {
    // Mercedes Sprinter defaults to No TV when offered.
    if (family.slug === "mercedes-sprinter" && family.tvOptions.includes("no-tv")) return "no-tv";
    return family.tvOptions[0];
  });
  const [pickupDate, setPickupDate] = useState<string>(() => getSavedDates().pickupDate ?? "");
  const [returnDate, setReturnDate] = useState<string>(() => getSavedDates().returnDate ?? "");
  const [pickupTime, setPickupTime] = useState<string>(() => getSavedDates().pickupTime ?? "");
  const [returnTime, setReturnTime] = useState<string>(() => getSavedDates().returnTime ?? "");
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  // Persist as they type so other pages see the latest selections.
  useEffect(() => {
    saveDates({ pickupDate, returnDate, pickupTime, returnTime, passengerCount: passengers });
  }, [pickupDate, returnDate, pickupTime, returnTime, passengers]);

  // Sync from other components/tabs that change the shared trip dates.
  useEffect(() => {
    const sync = () => {
      const t = getSavedDates();
      if (t.pickupDate && t.pickupDate !== pickupDate) setPickupDate(t.pickupDate);
      if (t.returnDate && t.returnDate !== returnDate) setReturnDate(t.returnDate);
      if (t.pickupTime && t.pickupTime !== pickupTime) setPickupTime(t.pickupTime);
      if (t.returnTime && t.returnTime !== returnTime) setReturnTime(t.returnTime);
    };
    window.addEventListener("tripdates:change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("tripdates:change", sync);
      window.removeEventListener("storage", sync);
    };
  }, [pickupDate, returnDate, pickupTime, returnTime]);

  // Whenever the sheet opens, re-pull the latest shared dates so dates the
  // customer just entered in the location-page filter (or any other surface)
  // are pre-filled here instead of an empty field.
  useEffect(() => {
    if (!open) return;
    const t = getSavedDates();
    if (t.pickupDate) setPickupDate(t.pickupDate);
    if (t.returnDate) setReturnDate(t.returnDate);
    if (t.pickupTime) setPickupTime(t.pickupTime);
    if (t.returnTime) setReturnTime(t.returnTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When pickup changes and the existing return is no longer strictly after,
  // clear it so the user picks a fresh, valid value.
  useEffect(() => {
    if (pickupDate && returnDate && !isAfterYmd(returnDate, pickupDate)) {
      setReturnDate("");
    }
  }, [pickupDate, returnDate]);

  if (!open) return null;

  const today = todayYmd();
  const minReturn = pickupDate ? addDaysYmd(pickupDate, 1) : today;
  const days = daysBetweenYmd(pickupDate, returnDate);
  const datesValid = areDatesValid(pickupDate, returnDate);
  const dateError = pickupDate && returnDate && !datesValid
    ? "Return date must be after pickup date."
    : null;

  const goCheckout = (resolvedSlug: string, dailyRate: number | null | undefined) => {
    const vid = resolveCrmVehicleId(family, passengers, tv) ?? undefined;
    // Seed reserve flow so the checkout page opens pre-filled with the user's dates.
    try {
      const seed = {
        pickupDate,
        returnDate,
        pickupTime,
        returnTime,
        passengerCount: passengers,
      };
      sessionStorage.setItem(`sprinter-reservation-flow:${resolvedSlug}`, JSON.stringify(seed));
      saveDates({ pickupDate, returnDate, pickupTime, returnTime, passengerCount: passengers });
    } catch { /* noop */ }
    navigate({
      to: "/reserve/$slug",
      params: { slug: resolvedSlug },
      search: vid ? { vid } : undefined,
    });
  };

  const reservationSlug = `${city ?? "orlando"}-${family.reservationSuffix(passengers)}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white w-full md:w-[480px] max-w-[95vw] max-h-[92vh] overflow-y-auto rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-display font-bold text-[10px] uppercase tracking-[0.3em]" style={{ color: accent }}>
              Configure
            </p>
            <h2 className="font-display font-extrabold text-xl text-[#1a1a1a] leading-tight">
              {family.model}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1 text-[#1a1a1a]/60 hover:text-[#1a1a1a]" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <OptionGroup label="Passengers" icon={<Users size={12} />} accent={accent}>
          {family.passengerOptions.map((p) => (
            <OptionChip key={p} selected={passengers === p} onClick={() => setPassengers(p)} accent={accent}>
              {p}
            </OptionChip>
          ))}
        </OptionGroup>

        {(() => {
          const diag = getDiagram(family.slug, passengers, roof);
          const showTvPhoto = tv === "tv";
          return (
            <>
              <div className="mb-2 flex justify-end">
                <Link
                  to="/configure/$family"
                  params={{ family: family.slug }}
                  search={city ? { city } : undefined}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F97316] text-[#0F172A] text-xs font-display font-bold uppercase tracking-widest shadow-sm hover:bg-[#1a1a1a] transition-colors"
                >
                  More Info <ChevronRight size={14} />
                </Link>
              </div>
              <div className={`mb-3 grid gap-2 ${showTvPhoto ? "grid-cols-3" : "grid-cols-2"}`}>
                <div className="p-2 rounded-xl bg-[#1a1a1a]/[0.04] border border-[#1a1a1a]/10 flex items-center justify-center">
                  {diag ? (
                    <button
                      type="button"
                      onClick={() => setLightbox({ src: diag, alt: `${passengers}-Passenger ${family.model} diagram` })}
                      className="w-full flex items-center justify-center cursor-zoom-in"
                      aria-label="View larger image"
                    >
                      <img
                        src={diag}
                        alt={`${passengers}-Passenger ${family.model} diagram`}
                        className="w-full max-w-[200px] h-auto" loading="lazy" decoding="async" />
                    </button>
                  ) : (
                    <SeatingDiagram passengers={passengers} />
                  )}
                </div>
                <ExtraPhotoSlot familySlug={`${family.slug}:${passengers}`} accent={accent} onOpen={setLightbox} />
                {showTvPhoto && (
                  <ExtraPhotoSlot familySlug={`${family.slug}:${passengers}:tv`} accent={accent} onOpen={setLightbox} />
                )}
              </div>
            </>
          );
        })()}

        <OptionGroup label="TV" accent={accent}>
          {family.tvOptions.map((t) => (
            <OptionChip key={t} selected={tv === t} onClick={() => setTv(t)} accent={accent}>
              {TV_LABEL[t]}
            </OptionChip>
          ))}
        </OptionGroup>

        <OptionGroup label="Trip Dates & Times" accent={accent}>
          <div className="grid grid-cols-2 gap-2 w-full">
            <label className="flex flex-col">
              <span className="text-[10px] uppercase tracking-widest font-display font-bold text-[#1a1a1a]/60 mb-1">Pickup Date</span>
              <input
                type="date"
                value={pickupDate}
                min={today}
                onChange={(e) => setPickupDate(e.target.value)}
                className="border-2 rounded-lg px-2 py-1.5 text-sm font-display font-semibold bg-white text-[#1a1a1a]"
                style={{ borderColor: `${accent}55`, colorScheme: "light" }}
              />
            </label>
            <label className="flex flex-col">
              <span className="text-[10px] uppercase tracking-widest font-display font-bold text-[#1a1a1a]/60 mb-1">Pickup Time</span>
              <input
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="border-2 rounded-lg px-2 py-1.5 text-sm font-display font-semibold bg-white text-[#1a1a1a]"
                style={{ borderColor: `${accent}55`, colorScheme: "light" }}
              />
            </label>
            <label className="flex flex-col">
              <span className="text-[10px] uppercase tracking-widest font-display font-bold text-[#1a1a1a]/60 mb-1">Return Date</span>
              <input
                type="date"
                value={returnDate}
                min={minReturn}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && pickupDate && !isAfterYmd(v, pickupDate)) return;
                  setReturnDate(v);
                }}
                className="border-2 rounded-lg px-2 py-1.5 text-sm font-display font-semibold bg-white text-[#1a1a1a]"
                style={{ borderColor: `${accent}55`, colorScheme: "light" }}
              />
            </label>
            <label className="flex flex-col">
              <span className="text-[10px] uppercase tracking-widest font-display font-bold text-[#1a1a1a]/60 mb-1">Return Time</span>
              <input
                type="time"
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
                className="border-2 rounded-lg px-2 py-1.5 text-sm font-display font-semibold bg-white text-[#1a1a1a]"
                style={{ borderColor: `${accent}55`, colorScheme: "light" }}
              />
            </label>
          </div>
          {dateError && (
            <p className="mt-2 text-xs font-display font-bold text-red-600">{dateError}</p>
          )}
        </OptionGroup>

        <LivePriceBlock
          family={family}
          passengers={passengers}
          tv={tv}
          city={city ?? "orlando"}
          accent={accent}
          days={days}
          datesValid={datesValid}
          pickupDate={pickupDate || null}
          returnDate={returnDate || null}
          onCheckout={() => goCheckout(reservationSlug, null)}
        />
      </div>
      {lightbox && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/90 cursor-zoom-out"
          onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-[#0F172A]/6 text-[#0F172A] hover:bg-[#0F172A]/10"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <img src={lightbox.src} alt={lightbox.alt} className="max-w-[95vw] max-h-[90vh] object-contain" loading="lazy" decoding="async" />
        </div>
      )}
    </div>
  );
}

function OptionGroup({
  label,
  icon,
  accent,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <p className="font-display font-bold text-[10px] uppercase tracking-[0.25em] mb-2 inline-flex items-center gap-1.5" style={{ color: accent }}>
        {icon} {label}
      </p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function OptionChip({
  selected,
  onClick,
  accent,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 font-display font-bold text-xs px-3.5 py-2 rounded-xl border-2 transition ${
        selected ? "text-[#0F172A] shadow-md" : "text-[#1a1a1a] bg-white hover:border-current"
      }`}
      style={{
        borderColor: accent,
        backgroundColor: selected ? accent : undefined,
      }}
    >
      {selected && <Check size={14} />}
      {children}
    </button>
  );
}

function LivePriceBlock({
  family, passengers, tv, city, accent, days, datesValid, pickupDate, returnDate, onCheckout,
}: {
  family: VehicleFamily;
  passengers: number;
  tv: TVOption;
  city: string;
  accent: string;
  days: number;
  datesValid: boolean;
  pickupDate: string | null;
  returnDate: string | null;
  onCheckout: () => void;
}) {
  const { rate, vehicle, isLoading, isUnavailable } = useFamilyPricing(family, passengers, tv, city, pickupDate, returnDate);
  const rateAdjustments = useRateAdjustments(city, vehicle?.vehicle_type_id ?? null);
  if (isLoading) {
    return (
      <div className="mt-3 rounded-xl px-4 py-3 text-[#0F172A] text-sm" style={{ backgroundColor: accent }}>
        Loading live pricing…
      </div>
    );
  }
  if (isUnavailable || !rate || rate.daily_rate == null) {
    return (
      <div className="mt-3 rounded-xl px-4 py-3 text-[#0F172A]" style={{ backgroundColor: accent }}>
        <p className="font-display font-extrabold text-lg">Call for pricing</p>
        <a href="tel:9713282665" className="text-xs text-[#0F172A]/90 underline">(971) 328-2665</a>
      </div>
    );
  }
  const perDayMiles = rate.miles_included_per_day ?? 300;
  const totalMiles = perDayMiles * Math.max(1, days);
  const milesLabel = rate.unlimited_miles
    ? "Unlimited"
    : datesValid
    ? `${totalMiles.toLocaleString()} total (${perDayMiles}/day × ${days})`
    : `${perDayMiles}/day`;
  const extraMile = rate.extra_mile_rate != null ? `$${rate.extra_mile_rate}/mi` : "—";
  const subtotal = datesValid ? days * Number(rate.daily_rate) : 0;

  // Apply CRM-driven discounts/surcharges (duration, last_minute, low_availability).
  // Seasonal & event uplifts are already baked into rate.daily_rate by the CRM.
  let adjustmentLines: AppliedAdjustment[] = [];
  let total = subtotal;
  if (datesValid && subtotal > 0) {
    const result = applyRateAdjustments({
      base: subtotal,
      adjustments: (rateAdjustments.all ?? []).filter((a) => {
        const allowed = a.kind === "duration" || a.kind === "last_minute" || a.kind === "low_availability";
        if (!allowed) return false;
        const hay = `${(a.code ?? "").toLowerCase()} ${(a.name ?? "").toLowerCase()}`;
        if (hay.includes("clean")) return false;
        if (/season|holiday|event|peak|summer|winter|spring|fall|christmas|thanksgiving|easter/.test(hay)) return false;
        return true;
      }),
      citySlug: city,
      vehicleTypeId: vehicle?.vehicle_type_id ?? null,
      days,
      pickupDate: pickupDate || null,
    });
    adjustmentLines = result.lines;
    total = Math.round(result.adjustedBase);
  }

  return (
    <div className="mt-3 rounded-xl px-4 py-3" style={{ backgroundColor: accent }}>
      <div className="flex items-baseline justify-between">
        <p className="font-display font-bold text-xs uppercase tracking-wider text-[#0F172A]">Daily Rate</p>
        <p className="font-display font-extrabold text-xl text-[#0F172A]">
          ${rate.daily_rate}<span className="text-xs font-bold text-[#0F172A]/80 ml-1">/day</span>
        </p>
      </div>
      <div className="mt-2 pt-2 border-t border-white/30 flex items-baseline justify-between">
        <p className="font-display font-bold text-xs uppercase tracking-wider text-[#0F172A]">Miles Included</p>
        <p className="font-display font-extrabold text-sm text-[#0F172A] text-right">{milesLabel}</p>
      </div>

      <div className="mt-2 pt-2 border-t border-white/30 flex items-baseline justify-between">
        <p className="font-display font-bold text-xs uppercase tracking-wider text-[#0F172A]">Extra Mile</p>
        <p className="font-display font-extrabold text-sm text-[#0F172A]">{extraMile}</p>
      </div>
      {datesValid && adjustmentLines.map((a, i) => (
        <div key={`adj-${i}`} className="mt-2 pt-2 border-t border-white/30 flex items-baseline justify-between">
          <p className="font-display font-bold text-xs uppercase tracking-wider text-[#0F172A]">
            {a.name}{a.adjustment_type === "percent" ? ` (${a.adjustment_value > 0 ? "+" : ""}${a.adjustment_value}%)` : ""}
          </p>
          <p className="font-display font-extrabold text-sm text-[#0F172A] whitespace-nowrap">
            {a.amount < 0 ? "−" : ""}${Math.abs(a.amount).toFixed(2)}
          </p>

        </div>
      ))}
      {datesValid && (
        <div className="mt-2 pt-2 border-t border-white/30 flex items-baseline justify-between">
          <p className="font-display font-bold text-xs uppercase tracking-wider text-[#0F172A]">{days} {days === 1 ? "Day" : "Days"} Total</p>
          <p className="font-display font-extrabold text-2xl text-[#0F172A]">
            ${total.toLocaleString()}
          </p>
        </div>
      )}
      <button
        onClick={onCheckout}
        disabled={!datesValid}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-full bg-white hover:brightness-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ color: accent }}
      >
        {datesValid ? "Continue — Book or Get Quote" : "Pick dates to continue"}
      </button>
    </div>
  );
}


function ExtraPhotoSlot({ familySlug, accent, onOpen }: { familySlug: string; accent: string; onOpen?: (img: { src: string; alt: string }) => void }) {
  const src = SLOT_PHOTOS[familySlug];
  return (
    <div
      className="relative p-2 rounded-xl bg-[#1a1a1a]/[0.04] border border-dashed flex items-center justify-center overflow-hidden min-h-[120px]"
      style={{ borderColor: `${accent}66` }}
    >
      {src ? (
        <button
          type="button"
          onClick={() => onOpen?.({ src, alt: "Vehicle photo" })}
          className="w-full flex items-center justify-center cursor-zoom-in"
          aria-label="View larger photo"
        >
          <img src={src} alt="Vehicle photo" className="w-full max-h-[160px] object-contain" loading="lazy" decoding="async" />
        </button>
      ) : null}
    </div>
  );
}


